import React, { FC, useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import Slider from 'react-slick';
import styles from './MobileShopthelook.pcss';
import './BannerModal.scss';
import Text from '../../atoms/Text';
import SwipeIcon from '../../atoms/SwipeIcon';
import Card from '../ShopTheLook/Card';
import MobileShopthelookShimmer from './MobileShopthelookShimmer';
import ScrollBar from './ScrollBar';
import { readCookie, createCookie } from '../../../components/common/Cookie';
import usePrevious from '../../hooks/usePrevious';
import {
  gtmPagePush,
  gtmEventPush,
  luxeGtmProductImpressions
} from '../../../modules/ga/GTMdatalayer';
import { getProductIds, getBannerName, getProductsArray, sortProductList } from './utils';
import Video from '../../molecules/Video';
import { Feature, Variant } from '../ABFeature';
import { replaceWithJioCDN } from '../../../utils/imageHandling';

const cx = classNames.bind(styles);
interface ShopthelookProps {
  isLuxe: boolean;
  allBanners: string[];
  currentBannerInRow: number;
  variant: string;
  loading: boolean;
  getProductsDetails: (ids: string, index: number) => void;
  productsData: Record<string, import('../../types/product').Product[]>;
  priceRevealMetaInfo: Record<string, number>;
  priceRevealState: boolean;
  wishlistOptionCodeUpdates: boolean;
  currentStore: string;
  rowNumber: number;
  username: string;
  updateBannerDetails: ({ currentIndex }: { currentIndex: number }) => void;
  updateVideoDetails: ({
    name,
    loadTime
  }: {
    name: string;
    loadTime: number;
    currentIndex: undefined | number;
  }) => void;
}

const Shopthelook: FC<ShopthelookProps> = (props: ShopthelookProps) => {
  const {
    isLuxe,
    allBanners = [],
    currentBannerInRow,
    getProductsDetails,
    productsData,
    loading,
    variant,
    currentStore,
    rowNumber,
    priceRevealMetaInfo,
    priceRevealState,
    wishlistOptionCodeUpdates,
    updateBannerDetails,
    updateVideoDetails,
    username
  } = props;
  const [swipeVisibility, setSwipeVisibility] = useState(true);
  const [currentRowIndex, setCurrentRowIndex] = useState(currentBannerInRow);
  const [isDataUpdate, setDataUpdate] = useState(false);
  const platform = isLuxe ? 'msiteLuxe' : 'msite';
  const productKey = `${rowNumber}-${currentRowIndex}`;
  const products = sortProductList(productsData[productKey]);
  const [currentVideoDetails, setCurrentVideoDetails] = useState({
    videoName: '',
    video_load_time: 0,
    isAutoPlayEnabled: false
  });
  const [currentBannerMediaType, setCurrentBannerMediaType] = useState('VIDEO');

  useEffect(() => {
    if (currentBannerMediaType === 'VIDEO') {
      gtmPagePush('shop the look video screen', 'shop the look video screen', '', {
        store: isLuxe ? 'luxe' : 'ajio'
      });
    }
  }, [currentBannerMediaType]);

  useEffect(() => {
    if (!products) {
      const productIds = getProductIds(allBanners[currentRowIndex]);
      getProductsDetails(productIds, productKey);
    }
  }, [currentRowIndex]);
  useEffect(() => {
    if (products) {
      const ProductArray = getProductsArray(products, currentBannerMediaType);
      luxeGtmProductImpressions({
        impressions: ProductArray,
        page_name:
          currentBannerMediaType === 'IMAGE' ? 'shop the look' : 'shop the look video screen',
        page_type:
          currentBannerMediaType === 'IMAGE' ? 'shop the look' : 'shop the look video screen',
        store_type: isLuxe ? 'luxe' : 'ajio',
        fromVideoBanner: currentBannerMediaType !== 'IMAGE',
        video_name: currentVideoDetails.videoName || '',
        video_load_time: currentVideoDetails.video_load_time || 0,
        impressions: ProductArray
      });
      // setDataUpdate(true)
      // setTimeout(()=>setDataUpdate(false),2000)
    }
  }, [products, currentBannerMediaType]);
  useEffect(() => {
    const isSwipe = readCookie('isSwipe');
    if (isSwipe) {
      setSwipeVisibility(false);
    }
    setTimeout(() => {
      setScrollPosition();
    }, 1000);
  }, []);

  useEffect(() => {
    updateBannerDetails({ currentIndex: currentRowIndex });
    const bannerType = allBanners[currentRowIndex]?.dynamicPageMetadata?.mediaType || 'IMAGE';
    setCurrentBannerMediaType(bannerType);
  }, [currentRowIndex]);

  const setScrollPosition = () => {
    let position = localStorage.getItem('shopTheLookXPos');
    if (position) {
      const data = position?.split('-') || [];
      let element = document.getElementById(`scrollContainer${data[0]}`) || {};
      if (element) {
        element.scrollLeft = Number(data[1]);
      }
    }
  };

  const listenCurrentScrollPosition = (index: number) => {
    let div = document.getElementById(`scrollContainer${index}`) || {};
    localStorage.setItem('shopTheLookXPos', `${index}-${div.scrollLeft}`);
  };

  const prevCurrentRowIndex: number = usePrevious(currentRowIndex);
  const bannerName = getBannerName(allBanners[currentRowIndex]);
  useEffect(() => {
    if (currentRowIndex !== prevCurrentRowIndex) {
      if (currentBannerMediaType === 'IMAGE') {
        gtmEventPush({
          event: 'shop_the_look',
          event_category: 'Shop the Look',
          event_action:
            currentRowIndex - prevCurrentRowIndex < 0 ? 'Banner Swipe Down' : 'Banner Swipe Up',
          event_label: bannerName
        });
      } else {
        const { videoName, isAutoPlayEnabled, video_load_time } = currentVideoDetails;
        gtmEventPush({
          event: 'video_screen_interactions',
          event_category: 'shop the look video interactions',
          event_action: currentRowIndex - prevCurrentRowIndex < 0 ? 'previous video' : 'next video',
          video_name: videoName,
          is_auto_play_enabled: isAutoPlayEnabled,
          video_load_time,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: 'shop the look video screen',
          page_type: 'shop the look video screen'
        });
      }
    }
  }, [prevCurrentRowIndex, currentRowIndex]);
  const onSwiping = () => {
    createCookie('isSwipe', true);
    setSwipeVisibility(false);
  };

  // const shadowClass = swipeVisibility && allBanners?.length > 1 ? 'swipe-with-shadow' : 'shadow';
  const cardContainerClass = cx('card-container', 'shadow');
  // if (loading) {
  //   return <MobileShopthelookShimmer variant={variant} loading={loading} />;
  // }

  const selectedBannerData = allBanners[currentRowIndex];
  const { dynamicPageMetadata: { subTitle = '', title = ' ' } = {} } = selectedBannerData;
  const slickSettings = {
    dragable: true,
    initialSlide: currentRowIndex,
    infinite: false,
    arrows: false,
    dots: true,
    vertical: true,
    verticalSwiping: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: (current: number, next: number) => {
      if (swipeVisibility) onSwiping();
      setCurrentRowIndex(current);
    }
  };
  const updateCurrentVideoDetails = ({
    name,
    loadTime,
    currentIndex
  }: {
    name: string;
    loadTime: number;
    currentIndex: number;
  }) => {
    setCurrentVideoDetails({
      videoName: name,
      video_load_time: loadTime,
      isAutoPlayEnabled:
        allBanners[currentIndex - 1]?.dynamicPageMetadata?.videoSettings?.autoPlay || false
    });
    updateVideoDetails({ name, loadTime, currentIndex });
  };

  return (
    <div className={cx('model-container')}>
      <ScrollBar items={allBanners.length} currentItem={currentRowIndex} />
      {swipeVisibility && allBanners?.length > 1 && <SwipeIcon />}
      <div className={cx('header')}>
        <div className={cx('heading')}>
          <Text variant="T4" weight="bold" color="White">
            {subTitle}
          </Text>
        </div>
      </div>
      <div className={cx('image-container')} id="mobile-banner-images">
        <Slider {...slickSettings}>
          {allBanners?.map((val = {}, index) => {
            const {
              dynamicPageMetadata: {
                extendedImageUrl = '',
                mediaType = 'IMAGE',
                audioSettings: { muteOnFullScreen = true } = {},
                videoSettings: { autoPlay = true, loop = true, showTimestamp = true } = {}
              } = {},
              thumbnailImage = ''
            } = val;
            const itemkey = `${rowNumber}-${index}`;
            // [1, 2] are for two dummy shimmer unless data received
            const productsArray = sortProductList(productsData[itemkey]) || [1, 2];
            return (
              <div className={cx('slickItem')}>
                <div className={cx('imageDiv')}>
                  <div className="top-gradient-box" />
                  {mediaType === 'VIDEO' ? (
                    <Feature featureName="videoBannerShopTheLook">
                      <Variant id="0">{null}</Variant>
                      <Variant id="1">
                        <Video
                          autoPlay={autoPlay}
                          videoURL={extendedImageUrl}
                          thumbnailImage={thumbnailImage}
                          muted={muteOnFullScreen}
                          displayProgressBar
                          showPlayPauseHandler
                          isModal
                          onClickOpenModal={false}
                          displayTopGradient={false}
                          showTimestamp={false}
                          loop={loop}
                          rowNumber={rowNumber}
                          isShopThelook
                          currentIndex={currentRowIndex + 1}
                          updateVideoDetails={updateCurrentVideoDetails}
                          scrollBarExits={allBanners.length > 1}
                          deviceType="msite"
                        />
                      </Variant>
                    </Feature>
                  ) : (
                    <img
                      key={index}
                      src={replaceWithJioCDN(extendedImageUrl)}
                      alt="banner-img"
                      className={cx('banner-img')}
                    />
                  )}
                </div>
                <div className={cardContainerClass}>
                  <Text variant="T4" weight="bold" color="White" customClass={cx('title')}>
                    {title}
                  </Text>
                  <div
                    className={`${cx('cards', isDataUpdate && 'active-cards')} cardScroll`}
                    id={`scrollContainer${index}`}
                  >
                    {productsArray?.map((product, key) => (
                      <Card
                        product={product}
                        id={`shopTheLook ${key}`}
                        platform={platform}
                        variant={variant}
                        priceRevealMetaInfo={priceRevealMetaInfo}
                        priceRevealState={priceRevealState}
                        loading={loading}
                        wishlistOptionCodeUpdates={wishlistOptionCodeUpdates}
                        bannerName={bannerName}
                        isLuxe={isLuxe}
                        currentStore={currentStore}
                        username={username}
                        rowNumber={rowNumber}
                        currentRowIndex={currentRowIndex}
                        currentBannerMediaType={currentBannerMediaType}
                        currentVideoDetails={currentVideoDetails}
                        getCurrentScrollPosition={() => listenCurrentScrollPosition(index)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </Slider>
      </div>
    </div>
  );
};
export default Shopthelook;
